import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import { customTableStyles } from "../../assets/CustomTableStyles";
import Select from "react-select";

function ActivityAttendance() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const roleId = localStorage.getItem("roleId");
  const userId = localStorage.getItem("userId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      selectedDate: new Date().toISOString().split("T")[0],
    },
  });

  const [loading, setLoading] = useState(false);
  const [centerId, setCenterId] = useState(savedCenterId || null);
  // const [selectedActivity, setSelectedActivity] = useState(0);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const [activitiesData, setActivitiesData] = useState([]);
  const [activityAttendanceData, setActivityAttendanceData] = useState([]);
  const [isFetching, setIsFetching] = useState(false); // New loading state for fetching
  const [activities, setActivities] = useState([]);

  const selectedDate = watch("selectedDate");
  const selectedActivity = watch("selectedActivity");

  const getActivities = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetAllActivites?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        const activityOptions = response.activities.map((activity) => ({
          value: activity.sr, // Use sr as the unique key
          label: activity.activityName,
          actvityCategoryId: activity.actvityCategoryId,
          id: activity.id, // Include id if needed for getActivityAttendanceList
        }));
        setActivities(activityOptions);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Failed to load activities.");
    }
  };

  const getActivitiesList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetActivities?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActivitiesData(response);
    } catch (error) {
      console.error("Error fetching activities:", error);
    }
  };

  const getActivityAttendanceList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const userIdToSend = ["9", "10", "11"].includes(roleId) ? userId : 0;
    const activityId = selectedActivity?.id || 0;
    const actvityCategoryId = selectedActivity?.actvityCategoryId || 0;
    const dateToSend = selectedDate || "";

    try {
      setIsFetching(true); // Set fetching state
      const response = await get(
        `CampusActivity/GetActivityAttendanceList?centerId=${centerId}&ActivityDate=${dateToSend}&userId=${userIdToSend}&activityId=${activityId}&actvityCategoryId=${actvityCategoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const cleanedData = response?.activityAttendanceDtos?.map(
        (row, index) => ({
          ...row,
          rating: row.rating ?? 0,
          remarks: row.remarks ?? "",
          attendance: row.attendance ?? "P", // Default to "P"
          isCertificateGenerated: row.isCertificateGenerated ?? false,
          rowKey: `${row.campusActivityApplicationId}-${row.activityId}-${row.scheduleDateTime}-${index}`,
        })
      );

      setActivityAttendanceData(cleanedData || []);
    } catch (error) {
      console.error("Error fetching attendance list:", error);
      setActivityAttendanceData([]);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    getActivities();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setActivityAttendanceData([]);
      getActivityAttendanceList();
    } else {
      setActivityAttendanceData([]);
    }
  }, [selectedDate, centerId]);

  useEffect(() => {
    if (selectedDate) {
      getActivityAttendanceList();
    }
  }, [selectedActivity]);

  const handleStarClick = (rowId, newRating) => {
    const updatedData = activityAttendanceData.map((row) => {
      if (row.campusActivityApplicationId === rowId) {
        return { ...row, rating: newRating };
      }
      return row;
    });
    setActivityAttendanceData(updatedData);
  };

  const handleAttendanceChange = (rowId, value) => {
    setActivityAttendanceData((prevData) =>
      prevData.map((row) =>
        row.campusActivityApplicationId === rowId
          ? { ...row, attendance: value }
          : row
      )
    );
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "studentName",
        header: "Student",
        // size: 150,
      },
      {
        accessorKey: "activityName",
        header: "Activity",
        size: 200,
      },
      {
        accessorKey: "attendance",
        header: "Attendance",
        size: 120,
        Cell: ({ row }) => (
          <div style={{ width: "80px" }}>
            <select
              value={row.original.attendance || "P"}
              onChange={(e) =>
                handleAttendanceChange(
                  row.original.campusActivityApplicationId,
                  e.target.value
                )
              }
              className="form-select"
            >
              <option value="P">P</option>
              <option value="A">A</option>
            </select>
          </div>
        ),
      },
      {
        accessorKey: "rating",
        header: "Rating",
        // size: 150,
        Cell: ({ row }) => (
          <div>
            {Array.from({ length: 5 }, (_, index) => (
              <span
                key={index}
                className={`fa fa-star ${index < row.original.rating ? "checked" : ""}`}
                onClick={() =>
                  handleStarClick(row.original.campusActivityApplicationId, index + 1)
                }
                style={{
                  cursor: "pointer",
                  margin: "0 2px",
                  color: index < row.original.rating ? "gold" : "lightgray",
                }}
              ></span>
            ))}
          </div>
        ),
      },
      {
        accessorKey: "remarks",
        header: "Remarks",
        // size: 150,
        Cell: ({ row }) => (
          <input
            type="text"
            className="form-control"
            value={row.original.remarks || ""}
            onChange={(e) => {
              const updatedData = activityAttendanceData.map((item) =>
                item.campusActivityApplicationId ===
                row.original.campusActivityApplicationId
                  ? { ...item, remarks: e.target.value }
                  : item
              );
              setActivityAttendanceData(updatedData);
            }}
          />
        ),
      },
    ],
    [activityAttendanceData]
  );

  const exportToExcel = () => {
    const filteredData = activityAttendanceData.map((row, index) => ({
      "Sr No": index + 1,
      Student: row.studentName,
      Activity: row.activityName,
      Attendance: row.attendance,
      Rating: row.rating,
      Remarks: row.remarks,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ActivityAttendance");
    XLSX.writeFile(workbook, "ActivityAttendance.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Activity Attendance List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = activityAttendanceData.map((row, index) => {
      return [
        index + 1,
        row.studentName,
        row.activityName,
        row.attendance,
        row.rating,
        row.remarks,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("ActivityAttendanceList.pdf");
  };

  const saveData = async () => {
    console.log("Saving data:", activityAttendanceData);
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!selectedDate) {
      toast.error("Please select a date.");
      return;
    }

    setLoading(true);

    let hasErrors = false;

    const formData = activityAttendanceData.map((row) => {
      if (!row.attendance) {
        console.log(`Missing attendance for row:`, row);
        hasErrors = true;
        return null;
      }
      return {
        createdBy: localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
        campusActivityApplicationId: row.campusActivityApplicationId,
        attendance: row.attendance,
        attendanceDate: new Date(selectedDate).toISOString(),
        studentId: row.studentId,
        rating: row.rating?.toString(),
        remarks: row.remarks,
      };
    });

    const validFormData = formData.filter((item) => item !== null);

    if (hasErrors) {
      toast.error("Please fill in attendance for all students.");
      setLoading(false);
      return;
    }
    if (validFormData.length === 0) {
      toast.error("No valid attendance data to save.");
      setLoading(false);
      return;
    }

    try {
      const response = await post(
        "CampusActivity/UpdateActivityAttendance",
        validFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response) {
        toast.success(response.message || "Success!");
      }
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error("Failed to save attendance.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Activity Attendance</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <div className="row g-3 new-form-style">
              <div className="col-md-2">
                <label className="form-label form-label-new">
                  Select Activity
                </label>
                <Controller
                  name="selectedActivity"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={activities}
                      isClearable
                      onChange={(option) => field.onChange(option)}
                      value={field.value}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                    />
                  )}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-new">
                  Activity Date <span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name="selectedDate"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="date"
                      className="form-control"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          {selectedDate && !isFetching && (
            <>
              <CustomDatatableGrid
                columns={columns}
                data={activityAttendanceData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                getRowId={(row) => row.rowKey}
              />

              {activityAttendanceData.length > 0 && (
                <div className="mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    disabled={loading}
                    onClick={saveData}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
            </>
          )}
          {isFetching && <div>Loading...</div>}{" "}
          {/* Optional: Show loading indicator */}
        </div>
      </div>
    </>
  );
}

export default ActivityAttendance;
